import { authFetch } from "./authApi";
import { normalizeEventAttachments } from "../utils/eventAttachments";

const LOG_EVENTS_API_DEBUG = false;

function logEventsApiDebug(message, payload) {
  if (!LOG_EVENTS_API_DEBUG) return;
  if (payload === undefined) {
    console.log(message);
    return;
  }
  console.log(message, payload);
}

async function requestJson(path, options = {}) {
  const response = await authFetch(path, options);
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`API ${response.status} on ${path}: ${body || response.statusText}`);
  }
  return response.json();
}

// ---------------------------------------------------------
// FETCH EVENTS
// ---------------------------------------------------------
export async function fetchEvents() {
  return requestJson(`/events`);
}

function parseSseBlock(block) {
  const lines = String(block || "").split(/\r?\n/);
  let event = "message";
  const dataLines = [];

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line || line.startsWith(":")) {
      continue;
    }

    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
      continue;
    }

    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  return {
    event,
    data: dataLines.join("\n"),
  };
}

function toRealtimeUpdate(eventName, rawData) {
  if (!rawData) return null;

  let payload;
  try {
    payload = JSON.parse(rawData);
  } catch {
    return null;
  }

  if (eventName === "event.created") {
    return { type: "create", event: payload };
  }
  if (eventName === "event.updated") {
    return { type: "update", event: payload };
  }
  if (eventName === "event.deleted") {
    return { type: "delete", id: payload?.id || "" };
  }
  if (eventName === "event.reload") {
    return { type: "reload", reason: payload || {} };
  }

  return null;
}

export function subscribeToEventUpdates(onUpdate, onError) {
  const controller = new AbortController();
  let stopped = false;

  const sleep = (ms) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const stop = () => {
    if (stopped) return;
    stopped = true;
    controller.abort();
  };

  (async () => {
    let retryDelayMs = 1000;

    try {
      while (!stopped) {
        try {
          const response = await authFetch("/events/stream", {
            method: "GET",
            headers: { Accept: "text/event-stream" },
            signal: controller.signal,
            timeoutMs: 24 * 60 * 60 * 1000,
          });

          if (!response.ok) {
            const body = await response.text().catch(() => "");
            throw new Error(`API ${response.status} on /events/stream: ${body || response.statusText}`);
          }

          const reader = response.body?.getReader?.();
          if (!reader) {
            throw new Error("SSE stream reader is not available in this browser");
          }

          retryDelayMs = 1000;
          const decoder = new TextDecoder();
          let buffer = "";

          while (!stopped) {
            const { value, done } = await reader.read();
            if (done) {
              throw new Error("SSE stream ended unexpectedly");
            }

            buffer += decoder.decode(value, { stream: true });
            const blocks = buffer.split(/\r?\n\r?\n/);
            buffer = blocks.pop() || "";

            for (const block of blocks) {
              const parsed = parseSseBlock(block);
              const update = toRealtimeUpdate(parsed.event, parsed.data);
              if (update) {
                onUpdate?.(update);
              }
            }
          }
        } catch (error) {
          if (stopped || error?.name === "AbortError") {
            break;
          }

          onError?.(error);

          // Stop retrying on auth failures; the auth layer already signals sign-out.
          const message = String(error?.message || "");
          if (message.includes("API 401") || message.includes("API 403")) {
            break;
          }

          await sleep(retryDelayMs);

          retryDelayMs = Math.min(Math.round(retryDelayMs * 1.8), 15000);
        }
      }
    } catch {
      // No-op: all operational errors are handled in the reconnect loop above.
    }
  })();

  return stop;
}

// ---------------------------------------------------------
// CREATE EVENT — FIXED (extendedProps now included)
// ---------------------------------------------------------
export async function createEvent(data) {
  const attachments = normalizeEventAttachments(data.extendedProps?.attachments);

  const payload = {
    title: data.title,
    start: data.start,
    end: data.end,
    extendedProps: {
      create_request_id: data.extendedProps?.create_request_id ?? "",
      project_id: data.extendedProps?.project_id ? String(data.extendedProps.project_id) : "",
      project_name: data.extendedProps?.project_name ?? "",
      is_lesson: data.extendedProps?.is_lesson ?? false,
      no_show: data.extendedProps?.no_show ?? false,
      fb_time_entry_id: data.extendedProps?.fb_time_entry_id ?? "",
      invoice_notes: data.extendedProps?.invoice_notes ?? "",
      internal_notes: data.extendedProps?.internal_notes ?? "",
      location: data.extendedProps?.location ?? "",
      category_id: data.extendedProps?.category_id ?? "",
      category_name: data.extendedProps?.category_name ?? "",
      category_color: data.extendedProps?.category_color ?? "",
      series_id: data.extendedProps?.series_id ?? "",
      repeat_rule: data.extendedProps?.repeat_rule ?? "",
      repeat_until: data.extendedProps?.repeat_until ?? "",
      repeat_end_mode: data.extendedProps?.repeat_end_mode ?? "",
      repeat_periods: data.extendedProps?.repeat_periods ?? 3,
      reminder_enabled: data.extendedProps?.reminder_enabled ?? false,
      reminder_config: data.extendedProps?.reminder_config ?? "",
      attachments,
    },
  };

  logEventsApiDebug("📤 API createEvent payload:", payload);

  return requestJson(`/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------
// UPDATE EVENT — FIXED (extendedProps now included)
// ---------------------------------------------------------
export async function updateEvent(id, data) {
  const payload = {
    title: data.title,
    start: data.start,
    end: data.end,
  };

  if (data.extendedProps) {
    const source = data.extendedProps;
    const next = {};
    const hasRecurrenceField =
      "repeat_rule" in source ||
      "repeat_until" in source ||
      "repeat_interval" in source ||
      "repeat_end_mode" in source ||
      "repeat_periods" in source;

    if ("project_id" in source) {
      next.project_id = source.project_id ? String(source.project_id) : "";
    }
    if ("project_name" in source) next.project_name = source.project_name ?? "";
    if ("is_lesson" in source) next.is_lesson = source.is_lesson ?? false;
    if ("no_show" in source) next.no_show = source.no_show ?? false;
    if ("fb_time_entry_id" in source) next.fb_time_entry_id = source.fb_time_entry_id ?? "";
    if ("invoice_notes" in source) next.invoice_notes = source.invoice_notes ?? "";
    if ("internal_notes" in source) next.internal_notes = source.internal_notes ?? "";
    if ("location" in source) next.location = source.location ?? "";
    if ("category_id" in source) next.category_id = source.category_id ?? "";
    if ("category_name" in source) next.category_name = source.category_name ?? "";
    if ("category_color" in source) next.category_color = source.category_color ?? "";
    if ("series_id" in source) next.series_id = source.series_id ?? "";
    if (hasRecurrenceField) {
      const repeatRule = source.repeat_rule ?? "";
      const repeatUntil = source.repeat_until ?? "";
      const repeatInterval = Math.max(1, Number(source.repeat_interval ?? 1) || 1);
      const repeatPeriods = Math.max(1, Number(source.repeat_periods ?? 3) || 3);
      const repeatEndMode =
        source.repeat_end_mode ||
        (repeatRule === "forever" ? "forever" : repeatUntil ? "until" : "frequency");

      next.repeat_rule = repeatRule;
      next.repeat_until = repeatEndMode === "forever" ? "" : repeatUntil;
      next.repeat_interval = repeatInterval;
      next.repeat_end_mode = repeatEndMode;
      next.repeat_periods = repeatPeriods;
    }
    if ("reminder_enabled" in source) next.reminder_enabled = source.reminder_enabled ?? false;
    if ("reminder_config" in source) next.reminder_config = source.reminder_config ?? "";
    if ("attachments" in source) {
      next.attachments = normalizeEventAttachments(source.attachments);
    }

    payload.extendedProps = next;
  }

  logEventsApiDebug("📤 API updateEvent payload:", {
    id,
    repeat_rule: payload.extendedProps?.repeat_rule,
    repeat_until: payload.extendedProps?.repeat_until,
    repeat_end_mode: payload.extendedProps?.repeat_end_mode,
    repeat_periods: payload.extendedProps?.repeat_periods,
    fullPayload: payload,
  });

  return requestJson(`/events/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------
// DELETE EVENT
// ---------------------------------------------------------
export async function deleteEvent(id) {
  return requestJson(`/events/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------
// DELETE ENTIRE SERIES
// ---------------------------------------------------------
export async function deleteSeriesAll(seriesId) {
  return requestJson(`/events/series/${encodeURIComponent(seriesId)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------
// DELETE SERIES FROM DATE ONWARDS
// ---------------------------------------------------------
export async function deleteSeriesFrom(seriesId, fromDate) {
  return requestJson(
    `/events/series/${encodeURIComponent(seriesId)}/from?from_date=${encodeURIComponent(fromDate)}`,
    {
      method: "DELETE",
    }
  );
}
